import React from 'react';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { useHistory } from 'react-router-dom';
import { paths } from '../../app/constants';
import { useSelector } from 'react-redux';
import { getFeatures } from '../../store/session/selectors';
import { FeatureEnum } from '../../models';
import { ListGroup, ListGroupItem } from 'reactstrap';
import { getSettings } from '../../store/settings/selectors';
import AppButton from '../../components/AppButton';

export type SettingItemId =
    | 'general'
    | 'services'
    | 'users'
    | 'reminders'
    | 'professional-reminders'
    | 'task-management'
    | 'extras'
    | 'reports'
    | 'packs'
    | 'payment-profiles'
    | 'automatic-payments'
    | 'cash-register'
    | 'clients-login'
    | 'consent-management'
    | 'waiting-list'
    | 'clients-intelligent-categorization'
    | 'invoices'
    | 'gohighlevel'
    | 'mailerlite'
    | 'webhook'
    | 'surveys'
    | 'google-calendar'
    | 'prescriptions'
    | 'account';

interface SettingItem {
    id: SettingItemId;
    titleKey: TK;
    requireFeatures?: FeatureEnum[];
    isNew?: boolean;
}

interface SettingsCategory {
    categoryTitleKey: TK;
    items: SettingItem[];
}

export const categories: SettingsCategory[] = [
    {
        categoryTitleKey: TK.settingsCategoryGeneral,
        items: [
            { id: 'general', titleKey: TK.company },
            { id: 'services', titleKey: TK.services },
            { id: 'users', titleKey: TK.users },
            { id: 'account', titleKey: TK.accountAndSubscription },
        ]
    },
    {
        categoryTitleKey: TK.settingsCategoryBilling,
        items: [
            { id: 'automatic-payments', titleKey: TK.automaticPayments, requireFeatures: [FeatureEnum.AutomaticPayments] },
            { id: 'payment-profiles', titleKey: TK.paymentProfiles, requireFeatures: [FeatureEnum.Billing] },
            { id: 'cash-register', titleKey: TK.cashRegister },
            { id: 'invoices', titleKey: TK.billingInvoices },
        ]
    },
    {
        categoryTitleKey: TK.settingsCategoryFeatures,
        items: [
            { id: 'packs', titleKey: TK.packsAndSubscriptions, requireFeatures: [FeatureEnum.Packs] },
            { id: 'extras', titleKey: TK.extrasAndProducts },
            { id: 'reports', titleKey: TK.reportsAndDocuments, requireFeatures: [FeatureEnum.Reports] },
            { id: 'surveys', titleKey: TK.surveys, requireFeatures: [FeatureEnum.Surveys] },
            { id: 'prescriptions', titleKey: TK.prescriptions, requireFeatures: [FeatureEnum.Prescriptions] },
        ]
    },
    {
        categoryTitleKey: TK.settingsCategoryClients,
        items: [
            { id: 'reminders', titleKey: TK.clientReminders, requireFeatures: [FeatureEnum.Reminders] },
            { id: 'clients-login', titleKey: TK.clientsPortalsAndMintu, requireFeatures: [FeatureEnum.ClientsLogin] },
            { id: 'consent-management', titleKey: TK.consentManagement, requireFeatures: [FeatureEnum.ConsentManagement] },
            { id: 'waiting-list', titleKey: TK.waitingListSettings },
            { id: 'clients-intelligent-categorization', titleKey: TK.intelligentCategorization, requireFeatures: [FeatureEnum.CRM] },
        ]
    },
    {
        categoryTitleKey: TK.settingsCategoryProfessionals,
        items: [
            { id: 'professional-reminders', titleKey: TK.professionalReminders, requireFeatures: [FeatureEnum.Reminders] },
            { id: 'task-management', titleKey: TK.taskManagement, requireFeatures: [FeatureEnum.Reminders] },
        ]
    },
    {
        categoryTitleKey: TK.settingsCategoryIntegrations,
        items: [
            { id: 'gohighlevel', titleKey: 'GoHighLevel' as TK, requireFeatures: [FeatureEnum.ThirdPartyIntegrations] },
            { id: 'mailerlite', titleKey: TK.mailerLite, requireFeatures: [FeatureEnum.ThirdPartyIntegrations] },
            { id: 'webhook', titleKey: 'Webhook' as TK, requireFeatures: [FeatureEnum.ThirdPartyIntegrations] },
            { id: 'google-calendar', titleKey: TK.googleSettingsTitle, isNew: true },
        ]
    },
];

export const settingsItems: Record<SettingItemId, SettingItem & {categoryTitleKey: TK}> = Object.fromEntries(categories
    .reduce<[SettingItemId, SettingItem & {categoryTitleKey: TK}][]>((acc, curr) => [
        ...acc,
        ...curr.items.map<[SettingItemId, SettingItem & {categoryTitleKey: TK}]>(i => [i.id, {...i, categoryTitleKey: curr.categoryTitleKey}]),
    ], [])) as Record<SettingItemId, SettingItem & {categoryTitleKey: TK}>

const SettingsMenu: React.FC = () => {
    const t = useTranslations();
    const history = useHistory();
    const features = useSelector(getFeatures);
    const settings = useSelector(getSettings);

    const handleSettingClick = (settingId: SettingItemId) => {
        history.push(`${paths.settings()}/${settingId}`);
    };

    return (
        <>
            <style>{`
                .settings-menu-root {
                    --settings-primary: #2ab7af;
                    --settings-secondary: #009385;
                    --settings-border: rgba(0, 147, 133, 0.14);
                    --settings-shadow: 0 10px 30px rgba(0, 147, 133, 0.08);
                    margin-top: var(--spacer-M);
                }

                .settings-menu-grid {
                    display: grid;
                    gap: var(--spacer-M);
                    grid-template-columns: 1fr;
                }

                .settings-menu-category {
                    background: linear-gradient(180deg, #ffffff 0%, #fbfefe 100%);
                    border: 1px solid var(--settings-border);
                    border-radius: 14px;
                    box-shadow: var(--settings-shadow);
                    padding: var(--spacer-S);
                }

                .settings-menu-title {
                    color: var(--settings-secondary);
                    margin-bottom: var(--spacer-S);
                    font-size: 1.25rem;
                    font-weight: 700;
                    letter-spacing: 0.2px;
                }

                .settings-menu-list {
                    border-radius: 10px;
                    overflow: hidden;
                    border: 1px solid var(--settings-border);
                }

                .settings-menu-item {
                    cursor: pointer;
                    border-left: 0;
                    border-right: 0;
                    transition: background-color 0.2s ease, color 0.2s ease, padding-left 0.2s ease;
                }

                .settings-menu-item:first-child {
                    border-top: 0;
                }

                .settings-menu-item:last-child {
                    border-bottom: 0;
                }

                .settings-menu-item:hover,
                .settings-menu-item:focus {
                    background-color: rgba(42, 183, 175, 0.1);
                    color: var(--settings-secondary);
                    padding-left: calc(var(--spacer-S) + 4px);
                }

                .settings-menu-footer {
                    font-size: 0.85rem;
                    margin-top: var(--spacer-M);
                    width: 100%;
                    text-align: end;
                    color: var(--text-body);
                }

                .settings-menu-beta-link {
                    color: var(--settings-secondary);
                    font-weight: 600;
                    text-decoration: none;
                }

                .settings-menu-beta-link:hover {
                    color: var(--settings-primary);
                }

                .settings-menu-version {
                    margin-top: 2px;
                }

                .settings-menu-item-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: var(--spacer-XS);
                }

                .settings-menu-new-badge {
                    font-size: 0.65rem;
                    font-weight: 800;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    padding: var(--spacer-XXS) var(--spacer-XS);
                    border-radius: 999px;
                    background: linear-gradient(135deg, var(--settings-primary), var(--settings-secondary));
                    color: var(--brand-accent);
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                @media (min-width: 768px) {
                    .settings-menu-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (min-width: 992px) {
                    .settings-menu-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }
            `}</style>
            <div className="settings-menu-root">
                <div className="settings-menu-grid">
                    {categories.map((category, catIndex) => {
                        const visibleItems = category.items.filter(item => item.requireFeatures?.every(f => features.includes(f)) ?? true);
                        if (visibleItems.length === 0) return null;

                        return (
                            <div key={catIndex} className="settings-menu-category">
                                <h4 className="settings-menu-title">
                                    {t(category.categoryTitleKey)}
                                </h4>
                                <ListGroup className="settings-menu-list">
                                    {visibleItems.map(item => (
                                        <ListGroupItem
                                            key={item.id}
                                            className="settings-menu-item"
                                            action
                                            onClick={() => handleSettingClick(item.id)}
                                        >
                                            <div className="settings-menu-item-row">
                                                <span>{t(item.titleKey as TK)}</span>
                                                {item.isNew && <span className="settings-menu-new-badge">{t(TK.new)}</span>}
                                            </div>
                                        </ListGroupItem>
                                    ))}
                                </ListGroup>
                            </div>
                        );
                    })}
                </div>

                <div className="settings-menu-footer">
                    <AppButton className="settings-menu-beta-link" color='link' href='https://beta.appointmint.pt' target='_blank'>Appointmint BETA</AppButton>
                    <div className="settings-menu-version"><b>Version:</b> {settings.appVersion}</div>
                </div>
            </div>
        </>
    );
};

export default SettingsMenu;
